import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import MainHeading from '@/components/common/MainHeading';
import Layout from '@/components/Layout/Layout';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { IOfficeResponse } from '../OfficeLocation/OfficeLocation';
import SearchIcon from '@/components/Icons/SearchIcon';
import FullPageLoader from '@/components/loader/FullPageLoader';
import {
  ISalaryConfigPageStates,
  ISalaryConfigResponse,
} from '@/interfaces/salary.model';
import { IFilterEmployee } from '@/interfaces/employee.model';
import { IDeleteResponse } from '@/interfaces/common.model';
import Button from '@/components/ui/Button';
import CardLayout from '@/components/Layout/CardLayout';
interface ISite {
  siteId: string;
  siteName: string;
}
const SalaryConfig = () => {
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [employees, setEmployees] = useState<IFilterEmployee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<IFilterEmployee[]>(
    [],
  );
  const [loadingSave, setLoadingSave] = useState(false);
  const [pageStates, setPageStates] = useState<ISalaryConfigPageStates>({
    siteList: [],
    loadingEmployees: false,
    loadingSites: false,
  });
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  useEffect(() => {
    getOffices();
  }, []);
  const getOffices = async () => {
    const filter = {
      type: 'office',
    };

    const api = `${API_URL}${API_ENDPOINT.getSite}?${getFilterUrl(filter)}`;

    try {
      setPageStates((p) => ({ ...p, loadingSites: true }));
      const response: IOfficeResponse = await callApi(api, {
        method: 'GET',
      });

      if (response.status && response.data?.site?.length) {
        setSelectedSiteId(response.data.site[0].id);
        handleSiteChange(response.data.site[0].id);
        setPageStates((p) => ({
          ...p,
          siteList: response?.data?.site || [],
          loadingSites: false,
        }));

        return response.data.site.map((office) => ({
          value: office.id,
          label: office.name,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching offices:', error);
      return [];
    } finally {
      setPageStates((p) => ({ ...p, loadingSites: false }));
    }
  };
  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase().trim();
    if (!searchTerm) return;
    if (searchTerm) {
      clearTimeout(Number(searchTimeout?.current));
    }
    searchTimeout.current = setTimeout(() => {
      if (!searchTerm) {
        setFilteredEmployees(employees);
      } else {
        setFilteredEmployees(
          employees.filter(
            (emp) =>
              emp.name?.toLowerCase().includes(searchTerm) ||
              emp.employeeCode?.toLowerCase().includes(searchTerm),
          ),
        );
      }
    }, 200);
  };

  const renderTable = () => {
    if (!selectedSiteId)
      return (
        <tr>
          <td colSpan={5} className="text-center text-muted py-4">
            Please select a site to view employees
          </td>
        </tr>
      );

    if (pageStates.loadingEmployees)
      return (
        <tr>
          <td colSpan={5} className="text-center text-muted py-4">
            Loading employees...
          </td>
        </tr>
      );

    if (filteredEmployees.length === 0)
      return (
        <tr>
          <td colSpan={5} className="text-center text-muted py-4">
            No employees found for this site
          </td>
        </tr>
      );

    return filteredEmployees.map((emp, i) => (
      <tr key={emp._key}>
        <td className="text-start">{String(i + 1).padStart(2, '0')}</td>
        <td className="text-start">{emp.employeeCode}</td>
        <td className="text-start">{emp.name}</td>
        <td style={{ width: '200px' }}>
          <input
            type="number"
            className="form-control"
            value={emp.dailyWages}
            onChange={(e) =>
              handleInputChange(emp._key, 'dailyWages', e.target.value)
            }
            min="0"
            step="0.01"
            placeholder="Daily Wages"
          />
        </td>
        <td style={{ width: '200px' }}>
          <input
            type="number"
            className="form-control"
            value={emp.grossAmount}
            onChange={(e) =>
              handleInputChange(emp._key, 'grossAmount', e.target.value)
            }
            min="0"
            step="0.01"
            placeholder="Gross Amount"
          />
        </td>
      </tr>
    ));
  };
  const handleInputChange = (key: string, field: string, value: string) => {
    setEmployees((prev) =>
      prev.map((emp) =>
        emp._key === key ? { ...emp, [field]: value || '' } : emp,
      ),
    );
    setFilteredEmployees((prev) =>
      prev.map((emp) =>
        emp._key === key ? { ...emp, [field]: value || '' } : emp,
      ),
    );
    setHasChanges(true);
  };
  const handleSiteChange = async (site: ISite | string) => {
    if (
      typeof site != 'string' &&
      'siteId' in site &&
      !site?.siteId &&
      typeof site != 'string'
    ) {
      setSelectedSiteId(null);
      setEmployees([]);
      return;
    }
    if (typeof site != 'string') {
      setSelectedSiteId(site.siteId);
    }
    setPageStates((p) => ({ ...p, loadingEmployees: true }));
    const siteId = typeof site == 'string' ? site : site?.siteId;
    try {
      const response: ISalaryConfigResponse = await callApi(
        `${API_URL + API_ENDPOINT.getSiteWiseUserSalary}?siteId=${siteId}`,
        { method: 'GET' },
      );

      if (response?.status) {
        if (response?.data?.length < 0) return;
        const list = response?.data
          ?.map((u, idx) => {
            if (!u) return undefined;

            const userId = u.id;

            let dailyWagesObj = null;
            let grossSalaryObj = null;

            for (const s of u.usersalaries || []) {
              const name = s?.sitesalaryhead?.salaryhead?.name?.toLowerCase();

              if (name === 'daily wages') {
                dailyWagesObj = s;
              } else if (name === 'gross salary') {
                grossSalaryObj = s;
              }

              if (dailyWagesObj && grossSalaryObj) break;
            }

            return {
              _key: userId ? `emp-${userId}` : `temp-${idx}`,
              id: userId,
              userId,
              employeeCode: u.employeeCode,
              name: u.name,
              dailyWages: dailyWagesObj?.amount ?? '',
              grossAmount: grossSalaryObj?.amount ?? '',
              dailyWagesHeadId: dailyWagesObj?.sitesalaryhead?.id ?? null,
              grossSalaryHeadId: grossSalaryObj?.sitesalaryhead?.id ?? null,
            };
          })
          .filter((item): item is IFilterEmployee => item !== undefined);

        setEmployees(list);
        setFilteredEmployees(list);
        renderTable();
      } else {
        setEmployees([]);
        showToastNotification('danger', 'Failed to fetch employees for site');
      }
    } catch {
      showToastNotification('danger', 'Error fetching employee data');
      setEmployees([]);
    } finally {
      setTimeout(() => {
        setPageStates((p) => ({ ...p, loadingEmployees: false }));
      }, 200);
    }
  };
  const handleSave = async () => {
    if (!selectedSiteId) {
      showToastNotification('danger', 'Please select a site first');
      return;
    }

    const invalid = employees.some(
      (e) =>
        !e.dailyWages ||
        !e.grossAmount ||
        e.dailyWages === '' ||
        e.grossAmount === '',
    );

    if (invalid) {
      showToastNotification(
        'danger',
        'Please fill all salary fields before saving',
      );
      return;
    }

    const payload = {
      salary: employees.map((e) => ({
        userId: e?.userId,
        userSalary: [
          {
            amount: String(e?.dailyWages),
            siteSalaryHeadId: e?.dailyWagesHeadId,
          },
          {
            amount: String(e?.grossAmount),
            siteSalaryHeadId: e?.grossSalaryHeadId,
          },
        ],
      })),
    };

    try {
      setLoadingSave(true);
      const res: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.updateSiteWiseUserSalary + selectedSiteId,
        { method: 'POST', data: payload },
      );
      if (res?.status) {
        setHasChanges(false);
        showToastNotification(
          'success',
          'Employee salary data saved successfully!',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setLoadingSave(false);
    }
  };
  return (
    <Layout>
      {(pageStates?.loadingSites ||
        pageStates?.loadingEmployees ||
        loadingSave) && <FullPageLoader />}

      <div className="content">
        <MainHeading title="Salary Config" />
        <CardLayout>
          <div className="table-head d-flex justify-content-between align-items-center mb-4 border-bottom pb-3 flex-wrap row-gap-3">
            <div className="position-relative">
              <span className="search-icon">
                <SearchIcon />
              </span>
              <input
                type="text"
                className="form-control search-control"
                id="searchUsers"
                placeholder={'Search by name'}
                onChange={handleSearch}
              />
            </div>
            <div className="d-flex gap-3 align-items-center flex-wrap">
              <div className="select-wrapper">
                <select
                  className="form-select"
                  value={selectedSiteId || ''}
                  onChange={(e) => {
                    const selected = pageStates?.siteList?.find(
                      (s) => String(s?.id) === e.target.value,
                    );
                    handleSiteChange({
                      siteId: e.target.value,
                      siteName: selected?.name || '',
                    });
                  }}
                >
                  {pageStates?.siteList?.length > 0 ? (
                    <>
                      <option value="" disabled>
                        Select office
                      </option>
                      {pageStates.siteList.map((s) => (
                        <option key={s?.id} value={s?.id}>
                          {s?.name}
                        </option>
                      ))}
                    </>
                  ) : (
                    <option disabled>No office found</option>
                  )}
                </select>
              </div>

              <Button
                className="btn btn-primary text-white"
                type="button"
                disabled={!hasChanges}
                onClick={handleSave}
              >
                <i className="fas fa-save me-2"></i>Save
              </Button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-border-bottom align-middle nowrap w-100">
              <thead className="table-light">
                <tr>
                  <th className="text-start">S.No</th>
                  <th className="text-start">Employee Code</th>
                  <th className="text-start">Employee Name</th>
                  <th className="text-start">Daily Wages (₹)</th>
                  <th className="text-start">Gross Amount (₹)</th>
                </tr>
              </thead>
              <tbody>{renderTable()}</tbody>
            </table>
          </div>
        </CardLayout>
      </div>
    </Layout>
  );
};
export default SalaryConfig;
